/**
 * Activity Log Writer
 *
 * Use this hook/function to WRITE activity logs (record user actions).
 * For READING activity logs (displaying in UI), use `useActivityLogs.ts` instead.
 *
 * @example
 * import { logActivity } from "@/hooks/useActivityLog";
 * await logActivity({ action: "sop_created", resourceId: sopId });
 */

import { supabase } from "@/integrations/supabase/client";
import { capturePostHogEvent } from "@/lib/posthog";

export type ActivityAction =
  | "login"
  | "logout"
  | "mfa_enabled"
  | "mfa_disabled"
  | "password_changed"
  | "email_changed"
  | "profile_updated"
  | "settings_updated"
  | "first_name_changed"
  | "last_name_changed"
  | "business_name_changed"
  | "website_changed"
  | "industry_changed"
  | "team_size_changed"
  | "timezone_changed"
  | "sop_created"
  | "sop_approved"
  | "sop_deleted"
  | "sop_updated"
  | "sop_version_restored"
  | "sop_version_deleted"
  | "sop_marked_draft"
  | "process_log_created"
  | "process_log_deleted"
  | "process_draft_saved"
  | "team_member_added"
  | "team_member_updated"
  | "team_member_deleted"
  | "automation_guide_generated"
  | "automation_guide_regenerated"
  | "automation_guide_deleted"
  | "data_deletion_requested"
  | "tracking_opt_out_enabled"
  | "tracking_opt_out_disabled"
  | "zapier_connected"
  | "zapier_disconnected";

interface LogActivityParams {
  action: ActivityAction;
  resourceType?: string;
  resourceId?: string;
  resourceName?: string;
  details?: Record<string, unknown>;
}

export async function logActivity({
  action,
  resourceType,
  resourceId,
  resourceName,
  details,
}: LogActivityParams): Promise<void> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    // Get user profile and org info
    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name, organization_id")
      .eq("id", user.id)
      .single();

    if (!profile?.organization_id) return;

    // Insert activity log
    const { error } = await supabase.from("activity_logs").insert({
      organization_id: profile.organization_id,
      user_id: user.id,
      user_email: user.email,
      user_name: profile.full_name,
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      resource_name: resourceName,
      details,
    });

    if (error) {
      console.error("Failed to log activity:", error);
      return;
    }

    capturePostHogEvent("zapido_activity_logged", {
      action,
      resource_type: resourceType || "none",
      has_resource_id: Boolean(resourceId),
      has_details: Boolean(details) && Object.keys(details ?? {}).length > 0,
    });
  } catch (error) {
    console.error("Failed to log activity:", error);
  }
}

export function useActivityLog() {
  return { logActivity };
}
